import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Section, Btn, Modal, Input } from "@/components/Shared";
import { Plus, Search, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { LeadDetailPanel } from "@/pages/Leads";

const STAGE_TONES = {
  New:        "bg-slate-100 text-slate-700 border-slate-200",
  Onboarding: "bg-sky-100 text-sky-800 border-sky-200",
  Pending:    "bg-amber-100 text-amber-800 border-amber-200",
  Approved:   "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected:   "bg-rose-100 text-rose-800 border-rose-200",
};

// Partner Onboarding — list of partner-submitted leads + "New onboarding" /
// "New MID for existing merchant" CTAs. Same lifecycle stages as the admin
// view but write surface is limited to: create + comment + file upload (to
// clear discrepancies). Stage changes are admin-only.

export function PartnerOnboarding() {
  const [data, setData] = useState({ rows: [], kpis: { total: 0, by_stage: {}, conversion_pct: 0 } });
  const [openLead, setOpenLead] = useState(null);
  const [createMode, setCreateMode] = useState(null);  // null | "new" | "existing"
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data: payload } = await api.get("/leads");
      setData(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    if (!q.trim()) return data.rows;
    const ql = q.trim().toLowerCase();
    return data.rows.filter((r) => r.name.toLowerCase().includes(ql) || (r.poc_name || "").toLowerCase().includes(ql));
  }, [data.rows, q]);

  const openDiscrepancyCount = data.rows.reduce((acc, r) => acc + ((r.comments || []).filter((c) => c.is_discrepancy && !c.resolved).length), 0);

  return (
    <div data-testid="partner-onboarding">
      <PageHeader
        title="Onboarding"
        subtitle="Onboard new merchants or request a new MID for an existing one — track every step."
        actions={
          <>
            <Btn variant="ghost" testid="po-new-mid-btn" onClick={() => setCreateMode("existing")}><Plus size={14} /> New MID (existing)</Btn>
            <Btn variant="accent" testid="po-new-onboard-btn" onClick={() => setCreateMode("new")}><Plus size={14} /> New onboarding</Btn>
          </>
        }
      />
      <div className="p-4 sm:p-10 max-w-5xl space-y-6">
        {openDiscrepancyCount > 0 && (
          <div className="p-3 rounded-sm border border-amber-200 bg-amber-50 text-amber-900 text-[12.5px] flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>You have <b>{openDiscrepancyCount}</b> open discrepanc{openDiscrepancyCount === 1 ? "y" : "ies"}. Click into the relevant lead to upload the requested document and add a clarifying comment.</div>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {["New", "Onboarding", "Pending", "Approved", "Rejected"].map((s) => (
            <div key={s} className="p-3 border border-[var(--line)] bg-[var(--surface)] rounded-sm">
              <div className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 inline-block rounded-sm border font-semibold ${STAGE_TONES[s]}`}>{s}</div>
              <div className="text-[20px] font-semibold mt-2">{data.kpis.by_stage?.[s] ?? 0}</div>
            </div>
          ))}
        </div>

        <Section
          title={`${visible.length} of ${data.rows.length} submissions`}
          right={
            <div className="relative w-full sm:w-64">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search merchant…" className="pl-8" data-testid="po-search" />
            </div>
          }
        >
          <div className="overflow-auto">
            <table className="table-data w-full">
              <thead><tr><th>Merchant</th><th>PG / LOB</th><th>Rate</th><th>Stage</th><th>Open issues</th><th>Updated</th><th></th></tr></thead>
              <tbody>
                {visible.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--muted)]">No submissions yet — click "New onboarding" to start.</td></tr>}
                {visible.map((r) => {
                  const open = (r.comments || []).filter((c) => c.is_discrepancy && !c.resolved).length;
                  return (
                    <tr key={r.id} className="cursor-pointer hover:bg-[var(--surface-2)]" onClick={() => setOpenLead(r.id)}>
                      <td className="font-medium">{r.name}{r.is_new_mid_for_existing && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">New MID</span>}</td>
                      <td className="text-[12px]">{r.requested_gateway || "—"} · {r.requested_lob || (r.eligible_lobs || []).join(", ") || "—"}</td>
                      <td className="num text-[12px]">{r.requested_rate != null ? `${(r.requested_rate * 100).toFixed(2)}%` : "—"}</td>
                      <td><span className={`text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${STAGE_TONES[r.stage]}`}>{r.stage}</span></td>
                      <td>{open > 0 ? <span className="text-[var(--negative)] text-[12px] font-semibold">{open}</span> : <span className="text-[var(--muted)] text-[12px]">—</span>}</td>
                      <td className="text-[11.5px] text-[var(--muted)]">{new Date(r.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                      <td><ArrowRight size={13} className="text-[var(--muted)]" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
      {createMode && <PartnerLeadForm mode={createMode} onClose={() => setCreateMode(null)} onCreated={() => { setCreateMode(null); load(); }} />}
      {openLead && <LeadDetailPanel leadId={openLead} isAdmin={false} onClose={() => { setOpenLead(null); load(); }} role="partner" />}
    </div>
  );
}

function PartnerLeadForm({ mode, onClose, onCreated }) {
  // Load partner-side data: their rate cards + admin-supported gateways
  const [rateInfo, setRateInfo] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [busy, setBusy] = useState(false);
  // For "existing" mode we let the partner pick from their existing tagged
  // merchants — auto-populates merchant_name. (Falls back to free text when
  // they're a brand new partner with no merchants yet.)
  const [myMerchants, setMyMerchants] = useState([]);
  const [f, setF] = useState({
    merchant_name: "",
    existing_merchant_name: "",
    gateway: "",
    lob: "",
    merchant_rate: "",
    rate_card_lob: "",
    contact_number: "",
    poc_name: "",
    email: "",
    website: "",
    notes: "",
  });
  useEffect(() => {
    Promise.all([
      api.get("/portal/partner/rate-cards"),
      api.get("/gateways"),
      // Partner sees only own merchants. /portal/partner/merchants returns
      // [{merchant_id, merchant_name, gateway, lob, ...}]; the older endpoint
      // /portal/partner/dashboard does NOT include merchants.
      api.get("/portal/partner/merchants").catch(() => ({ data: [] })),
    ]).then(([rc, g, ms]) => {
      setRateInfo(rc.data);
      // Restrict gateway dropdown to those that the admin has base rates for
      // — i.e. gateways with at least one entry in lob_rates. Per user spec:
      // "Only visible drop downs for Gateway selection should be those PGs
      //  where Admin has base rates like Cashfree, Rzp, Lyra".
      setGateways(g.data.filter((x) => Array.isArray(x.lob_rates) && x.lob_rates.length > 0));
      setMyMerchants(Array.isArray(ms.data) ? ms.data : []);
    });
  }, []);

  const isRevShare = (rateInfo?.compensation_model || "").toLowerCase() === "revenue_share";
  const cardsForGateway = (rateInfo?.rate_cards || []).filter((c) => c.gateway === f.gateway);
  // The (possibly) selected rate card row — used for client-side hint that
  // the merchant rate must be ≥ partner_base_rate. Admin-side validation is
  // authoritative; this hint just prevents trivial typos.
  const selectedCard = cardsForGateway.find((c) => c.lob === (f.rate_card_lob || f.lob));
  // LOB options sourced from the selected gateway's admin-configured rates.
  // We dedupe in case a gateway has duplicate LOB entries.
  const selectedGw = (gateways || []).find((g) => g.gateway_name === f.gateway);
  const lobOptions = useMemo(() => {
    if (!selectedGw) return [];
    return Array.from(new Set((selectedGw.lob_rates || []).map((r) => r.lob).filter(Boolean))).sort();
  }, [selectedGw]);

  const submit = async () => {
    if (mode === "existing" && !f.existing_merchant_name) { toast.error("Pick an existing merchant"); return; }
    if (mode === "new" && !f.merchant_name.trim()) { toast.error("Merchant name required"); return; }
    if (!f.gateway) { toast.error("Pick a gateway"); return; }
    if (!f.lob.trim()) { toast.error("LOB required"); return; }
    const ratePct = Number(f.merchant_rate);
    if (!ratePct || ratePct <= 0) { toast.error("Merchant rate required"); return; }
    if (ratePct > 100) { toast.error("Rate cannot exceed 100%"); return; }
    // Client-side floor for fixed-base partners (server is authoritative).
    // selectedCard.partner_base_rate is a decimal (e.g. 0.005 for 0.5%);
    // ratePct is in % (e.g. 0.5 for 0.5%) — compare in % space.
    if (!isRevShare && selectedCard && ratePct < (Number(selectedCard.partner_base_rate) * 100)) {
      toast.error(`Merchant rate must be at least ${(selectedCard.partner_base_rate * 100).toFixed(2)}%`);
      return;
    }
    setBusy(true);
    try {
      // Convert % → decimal at the API boundary.
      const merchant_rate = Number((ratePct / 100).toFixed(8));
      const body = {
        merchant_name: mode === "existing" ? "" : f.merchant_name.trim(),
        existing_merchant_name: mode === "existing" ? f.existing_merchant_name : null,
        gateway: f.gateway, lob: f.lob.trim(),
        merchant_rate,
        rate_card_lob: f.rate_card_lob || undefined,
        contact_number: f.contact_number, poc_name: f.poc_name,
        email: f.email || undefined, website: f.website, notes: f.notes,
      };
      await api.post("/leads/from-partner", body);
      toast.success("Submitted! Admin will review and update you here.");
      onCreated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Submission failed";
      // 409 duplicate-MID/lead errors are longer text — give them time to read.
      toast.error(detail, { duration: e?.response?.status === 409 ? 9000 : 4500 });
    }
    setBusy(false);
  };

  return (
    <Modal open={true} onClose={onClose} title={mode === "existing" ? "New MID for existing merchant" : "New merchant onboarding"} testid="partner-lead-form" size="lg">
      <div className="space-y-3">
        {mode === "existing" ? (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Existing merchant *</label>
            <select value={f.existing_merchant_name} onChange={(e) => setF({ ...f, existing_merchant_name: e.target.value })} data-testid="po-existing-merchant" className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]">
              <option value="">— Select —</option>
              {[...new Set(myMerchants.map((m) => m.merchant_name))].sort().map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        ) : (
          <Field label="Merchant name *" hint="The PG will issue the MID after onboarding — leave it to us." value={f.merchant_name} onChange={(v) => setF({ ...f, merchant_name: v })} testid="po-mname" />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Gateway *</label>
            <select value={f.gateway} onChange={(e) => setF({ ...f, gateway: e.target.value, rate_card_lob: "" })} data-testid="po-gw" className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]">
              <option value="">— Select —</option>
              {gateways.map((g) => <option key={g.gateway_name} value={g.gateway_name}>{g.gateway_name}</option>)}
            </select>
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Only PGs we currently support are listed.</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">LOB *</label>
            <select
              value={f.lob}
              onChange={(e) => setF({ ...f, lob: e.target.value, rate_card_lob: e.target.value })}
              disabled={!f.gateway}
              data-testid="po-lob"
              className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)] disabled:bg-[var(--surface-2)] disabled:text-[var(--muted)]"
            >
              <option value="">{f.gateway ? "— Select —" : "Pick a gateway first"}</option>
              {lobOptions.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            {f.gateway && lobOptions.length === 0 && (
              <div className="text-[10.5px] text-amber-700 mt-1">No LOBs configured for {f.gateway} — contact admin.</div>
            )}
          </div>
        </div>
        {!isRevShare && cardsForGateway.length > 0 && (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Use rate card</label>
            <select value={f.rate_card_lob} onChange={(e) => setF({ ...f, rate_card_lob: e.target.value })} data-testid="po-ratecard" className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]">
              <option value="">— Pick a rate card (optional) —</option>
              {cardsForGateway.map((c, i) => <option key={i} value={c.lob}>{c.lob} · your base {(c.partner_base_rate * 100).toFixed(2)}%</option>)}
            </select>
          </div>
        )}
        <Field label="Merchant rate (%) *" hint={isRevShare ? "Enter as % — e.g. 1.8 for 1.8% MDR. Floor verified by admin during review." : selectedCard ? `Enter as % — must be at least ${(selectedCard.partner_base_rate * 100).toFixed(2)}% (your base for ${selectedCard.gateway} · ${selectedCard.lob}).` : "Enter as % — e.g. 1.8 for 1.8% MDR"} value={f.merchant_rate} onChange={(v) => setF({ ...f, merchant_rate: v })} testid="po-rate" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="POC name" value={f.poc_name} onChange={(v) => setF({ ...f, poc_name: v })} testid="po-poc" />
          <Field label="Contact number" value={f.contact_number} onChange={(v) => setF({ ...f, contact_number: v })} testid="po-phone" />
          <Field label="Email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="po-email" />
          <Field label="Website" value={f.website} onChange={(v) => setF({ ...f, website: v })} testid="po-website" />
        </div>
        <Field label="Notes" multiline value={f.notes} onChange={(v) => setF({ ...f, notes: v })} testid="po-notes" />
        <div className="text-[11.5px] text-[var(--muted)] leading-relaxed border-l-2 border-[var(--line-2)] pl-3">
          For website login credentials, attach them as a comment after creation — keeps sensitive info contained to the lead's discussion thread.
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="accent" onClick={submit} disabled={busy} testid="po-submit">{busy ? "Submitting…" : "Submit"}</Btn>
      </div>
    </Modal>
  );
}

function Field({ label, hint, value, onChange, multiline, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">{label}</label>
      {multiline
        ? <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full px-3 py-2 mt-1 border border-[var(--line-2)] bg-[var(--surface)] rounded-sm text-[13px] outline-none focus:border-[var(--accent)]" data-testid={testid} />
        : <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />}
      {hint && <div className="text-[10.5px] text-[var(--muted)] mt-1">{hint}</div>}
    </div>
  );
}

// ============== MERCHANT — REQUEST NEW MID ===============================
//
// Same lifecycle backing — creates a `/api/leads/from-merchant` lead. The
// merchant only needs to specify which PG and LOB they want; admin pulls the
// rest from their existing setup. If the merchant is partner-tagged, the
// lead auto-includes the partner so they're looped in too.

export function MerchantNewMid() {
  const [data, setData] = useState({ rows: [], kpis: { total: 0, by_stage: {}, conversion_pct: 0 } });
  const [openLead, setOpenLead] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [gateways, setGateways] = useState([]);
  const [f, setF] = useState({ requested_gateway: "", requested_lob: "", purpose: "" });
  const [busy, setBusy] = useState(false);

  // LOB options pulled from the selected gateway's admin-configured rate list.
  const selectedGw = useMemo(
    () => gateways.find((g) => g.gateway_name === f.requested_gateway),
    [gateways, f.requested_gateway],
  );
  const lobOptions = useMemo(() => {
    if (!selectedGw) return [];
    return Array.from(new Set((selectedGw.lob_rates || []).map((r) => r.lob).filter(Boolean))).sort();
  }, [selectedGw]);

  const load = async () => {
    try {
      const { data: payload } = await api.get("/leads");
      setData(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => {
    load();
    api.get("/gateways").then(({ data: g }) => setGateways(g.filter((x) => Array.isArray(x.lob_rates) && x.lob_rates.length > 0))).catch(() => { /* silent */ });
  }, []);

  const submit = async () => {
    if (!f.requested_gateway || !f.requested_lob.trim()) { toast.error("Gateway and LOB required"); return; }
    setBusy(true);
    try {
      await api.post("/leads/from-merchant", { ...f, requested_lob: f.requested_lob.trim() });
      toast.success("Request submitted. Your account manager will be in touch.");
      setShowForm(false); setF({ requested_gateway: "", requested_lob: "", purpose: "" });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Submission failed";
      toast.error(detail, { duration: e?.response?.status === 409 ? 9000 : 4500 });
    }
    setBusy(false);
  };
  const openDiscrepancyCount = data.rows.reduce((acc, r) => acc + ((r.comments || []).filter((c) => c.is_discrepancy && !c.resolved).length), 0);

  return (
    <div data-testid="merchant-new-mid">
      <PageHeader
        title="MID Requests"
        subtitle="Request a new merchant ID across any of our supported payment gateways"
        actions={<Btn variant="accent" testid="mnm-new-btn" onClick={() => setShowForm(true)}><Plus size={14} /> Request new MID</Btn>}
      />
      <div className="p-4 sm:p-10 max-w-4xl space-y-6">
        {openDiscrepancyCount > 0 && (
          <div className="p-3 rounded-sm border border-amber-200 bg-amber-50 text-amber-900 text-[12.5px] flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>You have <b>{openDiscrepancyCount}</b> open discrepanc{openDiscrepancyCount === 1 ? "y" : "ies"}. Click into the relevant request to upload a document or add a comment.</div>
          </div>
        )}
        <Section title={`${data.rows.length} request${data.rows.length === 1 ? "" : "s"}`}>
          <div className="overflow-auto">
            <table className="table-data w-full">
              <thead><tr><th>Submitted</th><th>Gateway</th><th>LOB</th><th>Stage</th><th>Issues</th><th></th></tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--muted)]">No requests yet.</td></tr>}
                {data.rows.map((r) => {
                  const open = (r.comments || []).filter((c) => c.is_discrepancy && !c.resolved).length;
                  return (
                    <tr key={r.id} className="cursor-pointer hover:bg-[var(--surface-2)]" onClick={() => setOpenLead(r.id)}>
                      <td className="text-[11.5px] text-[var(--muted)]">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                      <td className="font-medium">{r.requested_gateway || "—"}</td>
                      <td>{r.requested_lob || "—"}</td>
                      <td><span className={`text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${STAGE_TONES[r.stage]}`}>{r.stage}</span></td>
                      <td>{open > 0 ? <span className="text-[var(--negative)] text-[12px] font-semibold">{open}</span> : <span className="text-[var(--muted)] text-[12px]">—</span>}</td>
                      <td><ArrowRight size={13} className="text-[var(--muted)]" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      {showForm && (
        <Modal open={true} onClose={() => setShowForm(false)} title="Request new MID" testid="mnm-form" size="md">
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Gateway *</label>
              <select value={f.requested_gateway} onChange={(e) => setF({ ...f, requested_gateway: e.target.value, requested_lob: "" })} data-testid="mnm-gw" className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]">
                <option value="">— Select —</option>
                {gateways.map((g) => <option key={g.gateway_name} value={g.gateway_name}>{g.gateway_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">LOB *</label>
              <select
                value={f.requested_lob}
                onChange={(e) => setF({ ...f, requested_lob: e.target.value })}
                disabled={!f.requested_gateway}
                data-testid="mnm-lob"
                className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)] disabled:bg-[var(--surface-2)] disabled:text-[var(--muted)]"
              >
                <option value="">{f.requested_gateway ? "— Select —" : "Pick a gateway first"}</option>
                {lobOptions.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              {f.requested_gateway && lobOptions.length === 0 && (
                <div className="text-[10.5px] text-amber-700 mt-1">No LOBs configured for {f.requested_gateway} — contact admin.</div>
              )}
            </div>
            <Field label="Purpose / notes" multiline hint="Why do you need this MID? Helps us prioritise." value={f.purpose} onChange={(v) => setF({ ...f, purpose: v })} testid="mnm-purpose" />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn variant="accent" onClick={submit} disabled={busy} testid="mnm-submit">{busy ? "Submitting…" : "Submit"}</Btn>
          </div>
        </Modal>
      )}
      {openLead && <LeadDetailPanel leadId={openLead} isAdmin={false} onClose={() => { setOpenLead(null); load(); }} role="merchant" />}
    </div>
  );
}
